// The loading screen (加载单屏): full screen over the page while the map (app.js) or an OtterBonk run
// (games-otterbonk.js) gets its art in and draws its first frames. An otter walks on a plank, the bar follows what has
// actually loaded (tasks with weights: images, the Phaser loader's own progress events, the scene's build, warm-up
// frames), and a tip rotates under it.
//   const L = createLoading({ title: '上木筏' });   // nothing on screen yet
//   const t = L.task(2); t(0.5); t.done();          // or L.track(promise, weight), L.phaser(scene.load, weight)
//   L.show();       // the user asked to go in: appears only if loading is still going after DELAY ms (a cached load never flashes)
//   await L.ready(() => settled, { task });  // waits for settled() (capped), warm-up frames, then fades out
//   L.close();      // at once (an error card must be seen; never hides one)
//   L.note(text);   // a status line under the bar (e.g. 服务器搬家中，3 秒后重连): reconnects stay visible
// (It imports looks.js by the same URL as everyone, so it shares that module instance.)
import { BODIES, buildFrame, lookFor, frames16, bodyOf, otterArtReady } from '/looks.js';
import { icon } from '/icons.js';
import { t, onLang } from '/i18n.js';

const DELAY = 250, FADE_IN = 180, FADE_OUT = 260;
/** The rotating tips (keys; the currently shown tip is redrawn when the language changes). */
export const TIPS = [
  'join.loading.tip.brushOtter', 'join.loading.tip.shaders', 'join.loading.tip.waxRaft', 'join.loading.tip.fishFridge', 'join.loading.tip.textures',
  'join.loading.tip.waves', 'join.loading.tip.shells', 'join.loading.tip.kelp', 'join.loading.tip.wakeOtter', 'join.loading.tip.mastBow',
  'join.loading.tip.pixels', 'join.loading.tip.oars', 'join.loading.tip.pearls', 'join.loading.tip.gulls', 'join.loading.tip.lifebuoy',
];
const CSS = `
.ldg{position:fixed;inset:0;z-index:70;display:none;align-items:center;justify-content:center;opacity:0;pointer-events:none;
 background:#2f84b8;transition:opacity ${FADE_IN}ms linear;font:14px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;
 padding:var(--st,0px) var(--sr,0px) var(--sb,0px) var(--sl,0px);box-sizing:border-box;overflow:hidden}
.ldg.arm{display:flex}
.ldg.on{opacity:1;pointer-events:auto}
.ldg.out{opacity:0;transition-duration:${FADE_OUT}ms;pointer-events:none}
.ldg .ldsea{position:absolute;inset:-64px 0 0 -64px;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='48'%3E%3Crect x='4' y='10' width='16' height='3' fill='%235fb6e6'/%3E%3Crect x='8' y='13' width='8' height='2' fill='%23bfe7fb' opacity='.5'/%3E%3Crect x='38' y='34' width='12' height='3' fill='%235fb6e6'/%3E%3C/svg%3E");
 background-size:64px 48px;animation:ldsea 3.2s steps(16) infinite}
@keyframes ldsea{to{transform:translateX(64px)}}
.ldg .ldcard{position:relative;width:min(340px,calc(100vw - 32px));box-sizing:border-box;background:#fff8e7;border:3px solid #1b1410;
 border-radius:18px;box-shadow:0 6px 0 #1b1410;padding:18px 22px 16px;text-align:center}
.ldg .ldtitle{font-weight:700;font-size:18px}
.ldg .ldstage{position:relative;height:118px;margin:4px auto 6px;width:180px}
.ldg .ldotter{position:absolute;left:50%;bottom:14px;width:96px;height:96px;margin-left:-48px;image-rendering:pixelated;animation:ldbob .44s steps(2) infinite}
@keyframes ldbob{50%{transform:translateY(-3px)}}
.ldg .ldraft{position:absolute;left:24px;right:24px;bottom:10px;height:12px;background:repeating-linear-gradient(90deg,#b07a45 0 22px,#8a5a2e 22px 24px);
 border:3px solid #1b1410;border-radius:4px;animation:ldrock 1.6s ease-in-out infinite}
@keyframes ldrock{50%{transform:rotate(-2deg) translateY(2px)}}
.ldg .ldwave{position:absolute;left:0;right:0;bottom:2px;height:10px;background:repeating-linear-gradient(90deg,#5fb6e6 0 8px,#bfe7fb 8px 12px,#5fb6e6 12px 20px);
 border-radius:4px;animation:ldwave 1s steps(5) infinite}
@keyframes ldwave{to{background-position:20px 0}}
.ldg .ldbar{height:18px;border:3px solid #1b1410;border-radius:8px;background:#e9d8b4;overflow:hidden;position:relative}
.ldg .ldbar i{position:absolute;left:0;top:0;bottom:0;width:0;background:repeating-linear-gradient(90deg,#FDD03B 0 10px,#f0b90b 10px 12px);transition:width .18s linear}
.ldg .ldpct{font:600 12px/1 ui-monospace,Menlo,monospace;margin-top:6px;color:#5b4a3a}
.ldg .ldtip{min-height:22px;margin-top:8px;display:flex;gap:6px;align-items:center;justify-content:center;transition:opacity .2s}
.ldg .ldtip.fade{opacity:0}
.ldg .ldnote{margin-top:6px;font-size:12px;color:#b91c1c;min-height:0}
.ldg .ldnote:empty{display:none}
@media (prefers-reduced-motion:reduce){.ldg .ldsea,.ldg .ldotter,.ldg .ldraft,.ldg .ldwave{animation:none}}`;
let styled = false;
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const raf = () => new Promise((r) => requestAnimationFrame(r));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** A walking otter's frames (32 x 32 canvases) for a look: facing down-right (so its face shows), the 3D walk. */
async function otterFrames(look, images) {
  const body = bodyOf(look); let tpl = images?.[body];
  if (!tpl?.naturalWidth) { tpl = new Image(); tpl.src = BODIES[body].sheet; await tpl.decode(); }
  const templates = []; templates[body] = tpl;
  return frames16('walk', 2).map((f) => buildFrame(templates, look, f));
}

/**
 * @param {object} [o]
 * @param {string} [o.title] the card's heading
 * @param {string} [o.seed] picks the otter's look (lookFor's seed); a random one if not given
 * @param {object} [o.look] a saved look to draw instead
 * @param {boolean} [o.waitForLook] do not show a random look while the caller retrieves the person's own
 */
export function createLoading({ title = t('join.loading.title'), seed = String(Math.random()), look = null, waitForLook = false } = {}) {
  if (!styled) { styled = true; const s = el('style'); s.id = 'ldg-css'; s.textContent = CSS; document.head.appendChild(s); }
  const root = el('div', 'ldg'), card = el('div', 'ldcard');
  root.setAttribute('role', 'progressbar'); root.setAttribute('aria-label', title); root.setAttribute('aria-valuemin', '0'); root.setAttribute('aria-valuemax', '100');
  const stage = el('div', 'ldstage'), otter = el('canvas', 'ldotter'); otter.width = otter.height = 32;
  stage.append(el('div', 'ldwave'), el('div', 'ldraft'), otter);
  const bar = el('div', 'ldbar'), fill = el('i'); bar.append(fill);
  const pct = el('div', 'ldpct', '0%'), tip = el('div', 'ldtip'), note = el('div', 'ldnote');
  const heading = el('div', 'ldtitle', title);
  card.append(heading, stage, bar, pct, tip, note);
  root.append(el('div', 'ldsea'), card);
  document.body.appendChild(root);

  const tasks = []; // [weight, fraction]
  let shown = 0, wanted = false, finished = false, closed = false, showTimer = 0, slowTimer = 0, tipTimer = 0, animId = 0, frames = null, drawn = 0;
  let selected = null, selectedKey = '', selectedImage = null, lookRevision = 0;
  const g = otter.getContext('2d'); g.imageSmoothingEnabled = false;

  function drawLook(appearance, images, revision) {
    // Do not hold boarding on a body or accessory image. A failed image leaves the loading screen usable.
    otterFrames(appearance, images).then((f) => { if (!closed && revision === lookRevision) frames = f; }).catch(() => {});
  }
  function setLook(appearance, images = null) {
    if (closed || !appearance) return;
    const key = JSON.stringify(appearance), image = images?.[bodyOf(appearance)] || null;
    if (key === selectedKey && image === selectedImage) return;
    selected = { appearance, images }; selectedKey = key; selectedImage = image; const revision = ++lookRevision;
    frames = null; g.clearRect(0, 0, 32, 32); // never keep a previous outfit while the new one loads
    drawLook(appearance, images, revision);
  }
  // The first frame can arrive before accessories; repaint when they settle, still without blocking the loading bar.
  otterArtReady.then(() => { if (!closed && selected) drawLook(selected.appearance, selected.images, lookRevision); });
  if (look || !waitForLook) setLook(look || lookFor(null, seed));

  function progress() {
    let w = 0, d = 0; for (const t of tasks) { w += t[0]; d += t[0] * t[1]; }
    return w ? d / w : 0;
  }
  function paint() {
    const p = finished ? 1 : progress();
    drawn = Math.max(drawn, p); // never backwards (tasks can be added on the way)
    const v = Math.round(drawn * 100);
    fill.style.width = v + '%'; pct.textContent = v + '%'; root.setAttribute('aria-valuenow', String(v));
  }
  let tipAt = Math.floor(Math.random() * TIPS.length);
  const drawTip = () => tip.replaceChildren(icon('bulb'), document.createTextNode(t('join.loading.tipLine', { tip: t(TIPS[tipAt]) })));
  function nextTip() {
    tip.classList.add('fade');
    setTimeout(() => { tipAt = (tipAt + 1 + Math.floor(Math.random() * 3)) % TIPS.length; drawTip(); tip.classList.remove('fade'); }, 200);
  }
  drawTip(); const stopLang = onLang(drawTip);
  function animate(now) {
    if (closed) return;
    if (frames?.length) { g.clearRect(0, 0, 32, 32); g.drawImage(frames[Math.floor(now / 120) % frames.length], 0, 0); }
    animId = requestAnimationFrame(animate);
  }

  /** A task worth `weight`: call it with how far along it is (0..1); .done() when it is. */
  function task(weight = 1) {
    const t = [weight, 0]; tasks.push(t);
    const set = (f) => { t[1] = Math.max(t[1], Math.min(1, f)); paint(); };
    set.done = () => set(1);
    return set;
  }
  const L = {
    el: root,
    task,
    setTitle(text) { heading.textContent = text; root.setAttribute('aria-label', text); },
    /** Update without waiting for art; older image requests cannot replace a newer look. */
    setLook,
    /** A promise as a task (a weight, or a task made earlier; done when it settles, either way). Returns the promise. */
    track(p, weight = 1) { const t = typeof weight === 'function' ? weight : task(weight); Promise.resolve(p).then(t.done, t.done); return p; },
    /** A Phaser LoaderPlugin (call from the scene's preload): its progress events drive a task (a weight, or a task
     *  made earlier, so the bar's total is known from the start). */
    phaser(load, weight = 3) {
      const t = typeof weight === 'function' ? weight : task(weight);
      load.on('progress', (v) => t(v)); load.once('complete', () => t.done());
      return t;
    },
    /** Asked to go in: shows the screen if loading is still going after DELAY ms. */
    show() {
      if (wanted || closed) return; wanted = true;
      showTimer = setTimeout(() => {
        if (finished || closed) return;
        shown = performance.now(); paint(); root.classList.add('arm'); void root.offsetWidth; root.classList.add('on');
        tipTimer = setInterval(nextTip, 1700); animId = requestAnimationFrame(animate);
        slowTimer = setTimeout(() => { if (!note.textContent) note.textContent = t('join.loading.slow'); }, 25000);
      }, DELAY);
    },
    get visible() { return !!shown && !closed; },
    note(text) { note.textContent = text || ''; },
    /**
     * Everything is in: wait (at most `cap` ms) for settled() to say the first view has what it draws, then a few frames
     * so the GPU has its textures and shaders (the hitch happens under the cover, not in the user's first second),
     * then fade out. Resolves once gone. Never shown (it all took under DELAY ms): it just goes.
     */
    async ready(settled = null, { cap = 2500, warm = 3, warmCap = 1200, task: w = task(1) } = {}) {
      if (closed) return;
      const t0 = performance.now();
      if (settled) while (!closed && !settled() && performance.now() - t0 < cap) { await raf(); w(Math.min(0.5, (performance.now() - t0) / cap)); }
      try { await Promise.race([document.fonts?.ready, sleep(500)]); } catch {}
      // warm-up: at least `warm` frames, and on until two frames in a row are quick (or warmCap runs out)
      const t1 = performance.now(); let last = await raf(), quick = 0, n = 0;
      while (!closed && (n < warm || quick < 2) && performance.now() - t1 < warmCap) {
        const now = await raf(); n++; quick = now - last < 34 ? quick + 1 : 0; last = now;
        w(0.5 + 0.5 * Math.min(1, n / warm));
      }
      L.warmMs = Math.round(performance.now() - t1);
      return L.finish();
    },
    /** Fade out now (the bar fills first). */
    async finish() {
      if (closed || finished) return;
      finished = true; clearTimeout(showTimer); paint();
      if (shown) {
        await sleep(120); // let the full bar be seen
        root.classList.add('out'); await sleep(FADE_OUT);
      }
      L.close();
    },
    /** Gone at once (an error card, the run closed). */
    close() {
      if (closed) return; closed = true; finished = true;
      clearTimeout(showTimer); clearTimeout(slowTimer); clearInterval(tipTimer); cancelAnimationFrame(animId); stopLang(); root.remove();
    },
  };
  return L;
}
